import { useState } from 'react';

/**
 * Runs `onChange` in the render where one of `sources` changes, once per
 * change, never on the first render.
 *
 * React's "adjust state when a value changes" recipe, replacing an effect
 * whose body is a `setState`. That effect runs after paint, so a stale value
 * flashes; setting state in render re-runs before commit. Required by
 * `react(set-state-in-effect)`.
 *
 * It skips the first render, so seed the initial value in `useState`.
 *
 * `sources` are compared with `Object.is`. An object or array literal rebuilt
 * every render never matches and loops; pass a query, prop or state value.
 */
export function useOnChange(
  sources: readonly unknown[],
  onChange: () => void,
): void {
  const [previous, setPrevious] = useState(sources);

  const changed =
    previous.length !== sources.length ||
    sources.some((source, index) => !Object.is(source, previous[index]));

  if (changed) {
    setPrevious(sources);
    onChange();
  }
}
